import { BaseDto } from '../base.dto'
import { PagingDto } from '../paging.dto'
import { TranslationDto } from '../translation.dto'
import { GovernTenantCategoryDto } from './tenant-category.dto'
import { ProductCategoryDto } from '../product-category.dto'
import { LocationDto } from '../location.dto'
import { MallDto } from '../mall.dto'
import { MallPublicDto } from './mall-public.dto'
import { FileDto } from '../file.dto'
import { TenantTypeDto } from '../tenant.dto'
import { FloorDto } from './floor.dto'
import { BaseError } from '../../errors'

// Translation interface for offline tenant
export interface OfflineTenantTranslation {
  languageCode: string
  name: string
  description: string
  operatingHour: string
  contact: string
  email: string
}

// Owner interface for offline tenant API response
export interface OfflineTenantOwner {
  id: string
  fullName: string
  email: string
  gender?: string | null
  enabled: boolean
  photo?: any | null
  phoneNumber?: string | null
  roleNames: string[]
}

export class GovernTenantDto extends BaseDto {
  // New properties for offline tenants
  translations?: OfflineTenantTranslation[]
  offlineCategoryId?: string
  logoId?: string
  storeListingImageId?: string
  featureImageId?: string
  floorId?: string
  displayOrder?: number
  enabled?: boolean
  mallId?: string
  tag?: string | null
  
  // Response properties from API (new structure)
  name?: TranslationDto
  description?: TranslationDto
  operatingHour?: TranslationDto
  contact?: TranslationDto
  email?: TranslationDto
  logo?: FileDto
  storeListingImage?: FileDto
  featureImage?: FileDto
  mall?: MallDto
  numberOfFavourites?: number
  offlineTenantCategory?: {
    id: string
    name: TranslationDto
  }
  
  // Food Ordering (BR003) - only for Offline Tenants in F&B Category
  orderingEnabled?: boolean
  fnbCategoryId?: string
  /** F&B Category display (from FK to F&BCategory) */
  fnbCategory?: { id: string; name: TranslationDto }
  // Legacy properties for backward compatibility
  location?: LocationDto
  paywayTenantId?: string
  telegramGroupId?: string
  cover?: FileDto
  mobileCover?: FileDto
  slug?: string
  adminEnabled?: boolean
  category?: GovernTenantCategoryDto
  productCategories?: ProductCategoryDto[]
  contactNumber?: string
  featuredCreatedAt?: Date
  whitelistedMid?: boolean
  isWhitelistSuccess?: boolean
  whitelistExisted?: boolean
  errorMessage?: string
  owners?: OfflineTenantOwner[]
  types?: TenantTypeDto[]
  selected?: boolean
  sortOrder?: number
  deeplink?: string

  static create({ id, logo, name, paywayTenantId, owners, types, telegramGroupId, whitelistedMid, description, cover, category, mobileCover }: GovernTenantDto) {
    return Object.assign(new GovernTenantDto(), {
      id: id || null,
      name: name || TranslationDto.create(),
      paywayTenantId,
      telegramGroupId,
      description: description || TranslationDto.create(),
      location: location || LocationDto.create(),
      mobileCover: mobileCover || null,
      category: category || null,
      logo: logo || null,
      cover: cover || null,
      whitelistedMid: whitelistedMid || null,
      owners: owners || [],
      types: types || []
    })
  }

  static update() {
    return new GovernTenantDto()
  }
}

export class CreateGovernTenantDto {
  translations: OfflineTenantTranslation[]
  offlineCategoryId: string
  logoId: string
  storeListingImageId: string
  featureImageId: string
  floorId: string
  displayOrder: number
  enabled: boolean
  mallId: string
  tag?: string | null
  // Legacy properties for backward compatibility
  name?: TranslationDto
  description?: TranslationDto
  locationId?: string
  paywayTenantId?: string
  telegramGroupId?: string
  categoryId?: string
  productCategoryIds?: string[]
  types?: string[]
  coverId?: string
  mobileCoverId?: string
}

export class UpdateGovernTenantDto {
  translations?: OfflineTenantTranslation[]
  offlineCategoryId?: string
  logoId?: string
  storeListingImageId?: string
  featureImageId?: string
  floorId?: string
  displayOrder?: number
  enabled?: boolean
  mallId?: string
  tag?: string | null
  /** Food Ordering (BR003) */
  orderingEnabled?: boolean
  fnbCategoryId?: string
  telegramGroupId?: string
  // Legacy properties for backward compatibility
  name?: TranslationDto
  description?: TranslationDto
  locationId?: string
  paywayTenantId?: string
  categoryId?: string
  productCategoryIds?: string[]
  coverId?: string
  mobileCoverId?: string
  adminEnabled?: boolean
  contactNumber?: string
  types?: string[]
}

export class FindGovernTenantDto extends PagingDto {
  languageCode?: string
  search?: string
  categoryId?: string
  locationId?: string
  mallIds?: string[]
  enabled?: string
  adminEnabled?: string
  /** Food Ordering Status filter: 'true' | 'false' */
  orderingEnabled?: string
  hasFloorMap?: string
  offlineCategoryId?: string
  floorId?: string
  getAll?: boolean
}

export class GovernTenantFilters {
  search: string
  categoryId: string
  locationId: string
  mallIds: string[]
  enabled: string
  adminEnabled: string
  languageCode: string
  offlineCategoryId: string
  floorId: string
}

export class GovernTenantState {
  tenants: GovernTenantDto[]
  isLoading: boolean
  error: BaseError | null
  pageInfo: {
    page: number
    pageSize: number
    collectionSize: number
    from: number
    to: number
  }
  filters: GovernTenantFilters
  categories: GovernTenantCategoryDto[]
  locations: LocationDto[]
  malls: MallPublicDto[]
  productCategories: ProductCategoryDto[]
  floors: FloorDto[]
  lastUpdated?: Date | null
}

export class SortOrderGovernTenantDto {
  tenantId: string
  sortOrder: number
}

export class GovernTenantResponseDto {
  isWhitelistSuccess: boolean
  whitelistExisted: boolean
  errorMessage: string
}

export class EnableTenantDto {
  id: string
}

export class DisableTenantDto {
  id: string
} 